import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  SelectOption,
  SelectList,
  SelectOptionProps,
  MenuToggle,
  MenuToggleElement,
  TextInputGroup,
  TextInputGroupMain,
  TextInputGroupUtilities,
  Button
} from '@patternfly/react-core';
import TimesIcon from '@patternfly/react-icons/dist/esm/icons/times-icon';

export interface BranchTypeaheadProps {
  value: string;                    // current selected branch
  onSelect: (value: string) => void;
  label: string;
  branchOptions: string[]; 
  placeholder?: string;
  /** Called each time the user changes the text in the input (before selection). */
  onInputChange?: (value: string) => void;
  /** Message shown when no options match the filter. Defaults to 'No branches match'. */
  noResultText?: string;
}

const BranchTypeahead: React.FC<BranchTypeaheadProps> = ({
  value,
  onSelect,
  label,
  branchOptions,
  placeholder,
  onInputChange,
  noResultText = 'No branches match',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string>(value);
  const [inputValue, setInputValue] = useState<string>(value);
  const [filterValue, setFilterValue] = useState<string>('');
  const [selectOptions, setSelectOptions] = useState<SelectOptionProps[]>([]);
  const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const NO_RESULTS = 'no-results';

  // Build the initial list from branchOptions
  useEffect(() => {
    const options = branchOptions.map(branch => ({
      value: branch,
      children: branch
    }));
    setSelectOptions(options);
  }, [branchOptions]);

  // Filter the select options whenever filterValue changes
  useEffect(() => {
    if (!filterValue) {
      // If filter is empty, show all
      const options = branchOptions.map(branch => ({
        value: branch,
        children: branch
      }));
      setSelectOptions(options);
      return;
    }

    const filtered = branchOptions.filter(b =>
      b.toLowerCase().includes(filterValue.toLowerCase())
    );
    if (filtered.length === 0) {
      setSelectOptions([
        {
          value: NO_RESULTS,
          children: `${noResultText} "${filterValue}"`,
          isAriaDisabled: true
        }
      ]);
    } else {
      setSelectOptions(
        filtered.map(branch => ({ value: branch, children: branch }))
      );
    }
  }, [filterValue, branchOptions]);

  // Keyboard navigation helpers
  const createItemId = (val: string) => `select-typeahead-${val.replace(' ', '-')}`;

  const setActiveAndFocusedItem = (itemIndex: number) => {
    setFocusedItemIndex(itemIndex);
    if (selectOptions[itemIndex]) {
      setActiveItemId(createItemId(String(selectOptions[itemIndex].value)));
    }
  };

  const resetActiveAndFocusedItem = () => {
    setFocusedItemIndex(null);
    setActiveItemId(null);
  };

  const closeMenu = () => {
    setIsOpen(false);
    resetActiveAndFocusedItem();
  };

  const selectOption = (optionValue: string | number, optionContent: string | number) => {
    setInputValue(String(optionContent));
    setFilterValue('');
    setSelected(String(optionValue));
    onSelect(String(optionValue));
    closeMenu();
  };

  const onSelectHandler = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined
  ) => {
    if (!value || value === NO_RESULTS) return;
    const optionText = selectOptions.find(opt => opt.value === value)?.children;
    selectOption(value, optionText as string);
  };

  const onTextInputChange = (_event: React.FormEvent<HTMLInputElement>, val: string) => {
    setInputValue(val);
    setFilterValue(val);
    resetActiveAndFocusedItem();
    if (onInputChange) onInputChange(val);
    if (!isOpen) {
      setIsOpen(true);
    }
    if (val !== selected) {
      setSelected('');
    }
  };

  const handleMenuArrowKeys = (key: string) => {
    if (!isOpen) {
      setIsOpen(true);
    }
    if (selectOptions.length === 0) return;

    let indexToFocus = focusedItemIndex ?? -1;

    if (key === 'ArrowUp') {
      indexToFocus = indexToFocus <= 0 ? selectOptions.length - 1 : indexToFocus - 1;
    } else if (key === 'ArrowDown') {
      indexToFocus = indexToFocus >= selectOptions.length - 1 ? 0 : indexToFocus + 1;
    }

    // skip disabled
    while (selectOptions[indexToFocus]?.isAriaDisabled) {
      indexToFocus = key === 'ArrowUp' ? indexToFocus - 1 : indexToFocus + 1;
      if (indexToFocus < 0) indexToFocus = selectOptions.length - 1;
      if (indexToFocus > selectOptions.length - 1) indexToFocus = 0;
    }

    setActiveAndFocusedItem(indexToFocus);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const focusedItem = focusedItemIndex !== null ? selectOptions[focusedItemIndex] : null;
    switch (event.key) {
      case 'Enter':
        if (isOpen && focusedItem && !focusedItem.isAriaDisabled) {
          selectOption(String(focusedItem.value), String(focusedItem.children));
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        handleMenuArrowKeys(event.key);
        break;
      default:
        break;
    }
  };

  const onToggleClick = () => {
    setIsOpen(!isOpen);
    textInputRef?.current?.focus();
  };

  const onClearButtonClick = () => {
    setSelected('');
    setInputValue('');
    setFilterValue('');
    resetActiveAndFocusedItem();
    textInputRef?.current?.focus();
  };

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      variant="typeahead"
      aria-label="Typeahead menu toggle"
      onClick={onToggleClick}
      isExpanded={isOpen}
      isFullWidth
    >
      <TextInputGroup isPlain>
        <TextInputGroupMain
          value={inputValue}
          onClick={() => setIsOpen(true)}
          onChange={onTextInputChange}
          onKeyDown={onInputKeyDown}
          id="typeahead-select-input"
          autoComplete="off"
          ref={textInputRef}
          placeholder={placeholder}
          role="combobox"
          isExpanded={isOpen}
        />
        <TextInputGroupUtilities style={!inputValue ? { display: 'none' } : {}}>
          <Button
            variant="plain"
            onClick={onClearButtonClick}
            aria-label="Clear input value"
            icon={<TimesIcon aria-hidden />}
          />
        </TextInputGroupUtilities>
      </TextInputGroup>
    </MenuToggle>
  );

  const renderLabel = (text: string | number) => {
    const s = String(text);
    const m = s.match(/^(\S+)\s+\((.+)\)$/);
    if (!m) return s;
    return (
      <span>
        {m[1]}
        <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontSize: 12 }}>({m[2]})</span>
      </span>
    );
  };

  return (
    <Select
      id="typeahead-select"
      isOpen={isOpen}
      selected={selected}
      onSelect={onSelectHandler}
      onOpenChange={(open) => {
        if (!open) {
          closeMenu();
        }
      }}
      popperProps={{ enableFlip: true, appendTo: () => document.body }}
      toggle={toggle}
    >
      <SelectList id="select-typeahead-listbox" style={{ maxHeight: '240px', overflowY: 'auto' }}>
        {selectOptions.map((option, index) => (
          <SelectOption
            key={String(option.value)}
            value={option.value}
            isFocused={focusedItemIndex === index}
            isDisabled={option.isAriaDisabled}
            id={createItemId(String(option.value))}
          >
            {renderLabel(option.children as string)}
          </SelectOption>
        ))}
      </SelectList>
    </Select>
  );
};

export default BranchTypeahead;
